import * as Sentry from '@sentry/nextjs'
import { addHours, startOfDay, startOfHour, subHours } from 'date-fns'
import { find, isEmpty, isString } from 'lodash-es'
import {
  ContextType,
  CSSProperties,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { LuEllipsisVertical, LuX } from 'react-icons/lu'

import { Eta } from '@/feature/eta'
import { TaskProgressPopout } from '@/feature/execution-board/components/TaskProgressPopout'
import { useTripUpdates } from '@/feature/execution-board/hooks/useTripUpdates'
import { FeatureFlag, useFlag, WhenFlag } from '@/feature/feature-flags'
import { GoogleLoader, MapEmbedding, MapHandler, MapSearchBar, reverseGeocode } from '@/feature/map'
import { LayerManager } from '@/feature/map/components/LayerManager'
import { MapIconLegend } from '@/feature/map/components/MapIconLegend'
import {
  ActiveAssetContext,
  AssetsAside,
  CollectiveAssetMarkers,
  GeofenceEventsModal,
  GeoFenceMarker,
  GeoFencesAside,
  MapSearchMarker,
  ObservatoryContext,
  useAreGeoFencesEnabled,
  useObservatoryPageStore,
  usePersistentObservatoryPageStore,
  useReplayConfig,
} from '@/feature/observatory'
import { createGeoFence, deleteGeoFence, editGeoFence } from '@/feature/observatory/api/geoFence'
import { AssetPanel } from '@/feature/observatory/components/AssetPanel/AssetPanel'
import { useMapDefaultPosition } from '@/feature/observatory/hooks/useMapDefaultPosition'
import { useObservatoryAssets } from '@/feature/observatory/hooks/useObservatoryAssets'
import { useObservatoryGeoFencesAside } from '@/feature/observatory/hooks/useObservatoryGeoFences'
import { Replay, ReplayContext, ReplayMapMessage, ReplayTimeline } from '@/feature/replay'

import { useElementHeight } from '@/lib/core/hooks/useElementHeight'
import { Asset } from '@/lib/features/Assets/interfaces'

import { useFocusedTask } from '../hooks/useFocusedTask'
import { useAreTasksEnabled, useObservatoryTasks } from '../hooks/useObservatoryTasks'
import styles from './ObservatoryPage.module.css'
import { RouterInitializer } from './RouterInitializer'
import { TaskAssetEnRouteLine } from './TaskAssetEnRouteLine'
import { TaskAssignedAssetMarker } from './TaskAssignedAssetMarker'
import { TaskMapOverlay } from './TaskMapOverlay/TaskMapOverlay'
import { TaskStartLayer } from './TaskStartLayer'

export const MapMessage = ({
  children,
  message,
  onClose,
}: {
  children?: ReactNode
  message?: string | ReactNode
  onClose?: () => void
}) => {
  message ||= children
  return (
    <div className={styles.mapMessageContainer}>
      {isString(message) || !message ? (
        <div className={styles.mapMessage}>
          <span className={styles.mapMessageText}>{message}</span>
          <button type="button" className={styles.mapMessageButton} onClick={onClose}>
            <LuX size="16" strokeWidth={1.5} />
            <span>Close</span>
          </button>
        </div>
      ) : (
        message
      )}
    </div>
  )
}

const useObservatoryReplayAsset = () => {
  const assets = useObservatoryAssets()
  const replayAssetId = usePersistentObservatoryPageStore((state) => state.replay.asset)

  return useMemo(() => {
    return find(assets.data?.results, { id: replayAssetId })
  }, [replayAssetId, assets.data?.results])
}

const Page = () => {
  const searchLocation = usePersistentObservatoryPageStore((state) => state.searchLocation)
  const setSearchLocation = usePersistentObservatoryPageStore((state) => state.setSearchLocation)

  const selectedItem = usePersistentObservatoryPageStore((state) => state.selectedItem)
  const setSelectedItem = usePersistentObservatoryPageStore((state) => state.setSelectedItem)

  const toggles = usePersistentObservatoryPageStore((state) => state.toggles)
  const vehicleLabels = usePersistentObservatoryPageStore((state) => state.vehicleLabels)
  const asides = usePersistentObservatoryPageStore((state) => state.asides)

  const replayAssetId = usePersistentObservatoryPageStore((state) => state.replay.asset)
  const setReplayAsset = usePersistentObservatoryPageStore((state) => state.replay.setAsset)

  // Store the selected asset before entering replay mode so we can restore it when replay ends
  const preReplaySelectedItem = useRef<typeof selectedItem>(undefined)

  const replaySelectionStartTime = usePersistentObservatoryPageStore(
    (state) => state.replay.selectionStart,
  )
  const replaySelectionEndTime = usePersistentObservatoryPageStore(
    (state) => state.replay.selectionEnd,
  )

  const geoFenceModalId = usePersistentObservatoryPageStore((state) => state.geoFenceModalId)
  const setGeoFenceModalId = usePersistentObservatoryPageStore((state) => state.setGeoFenceModalId)

  // The replay bar's height varies with its legend and labels, so it is measured rather than
  // assumed — see the CSS variable set on .mapContainer below.
  const { ref: replayBarRef, height: replayBarHeight } = useElementHeight<HTMLDivElement>()

  const updateMap = useObservatoryPageStore((state) => state.setMap)
  // Read back the map instance the callback above stores, so focus-mode actions can pan it.
  const map = useObservatoryPageStore((state) => state.map)
  const focusedTaskId = usePersistentObservatoryPageStore((state) => state.focusedTaskId)
  const setFocusedTaskId = usePersistentObservatoryPageStore((state) => state.setFocusedTaskId)
  const setAssetsSearch = usePersistentObservatoryPageStore((state) => state.setAssetsSearch)

  // Clear the Assets aside search whenever a task is focused, so the task's assigned vehicle isn't
  // filtered out of the (search-driven) asset list and can be shown on the map during focus.
  useEffect(() => {
    if (focusedTaskId) {
      setAssetsSearch('')
    }
  }, [focusedTaskId, setAssetsSearch])

  useEffect(() => {
    return () => {
      setSelectedItem(undefined)
      setFocusedTaskId(undefined)
    }
  }, [setSelectedItem, setFocusedTaskId])

  const showSearchMarker = useObservatoryPageStore((state) => state.showSearchMarker)
  const { mapMessage, setMapMessage } = useObservatoryPageStore(
    ({ mapMessage, setMapMessage }) => ({
      mapMessage,
      setMapMessage,
    }),
  )

  const assets = useObservatoryAssets()
  const geoFences = useObservatoryGeoFencesAside()

  const mapPosition = useMapDefaultPosition()
  const isGeoFencesEnabled = useAreGeoFencesEnabled()

  const replaySettings = useReplayConfig()

  const etaLocationFrom = usePersistentObservatoryPageStore((state) => state.eta.etaLocationFrom)
  const etaLocationTo = usePersistentObservatoryPageStore((state) => state.eta.etaLocationTo)
  const showEta = usePersistentObservatoryPageStore((state) => state.eta.showEta)

  const isTasksEnabled = useAreTasksEnabled()
  const { data: tasks } = useObservatoryTasks()

  const setEtaLocationFrom = usePersistentObservatoryPageStore(
    (state) => state.eta.setEtaLocationFrom,
  )
  const setEtaLocationTo = usePersistentObservatoryPageStore((state) => state.eta.setEtaLocationTo)
  const setEtaShow = usePersistentObservatoryPageStore((state) => state.eta.setShowEta)

  const etaOnClose = () => {
    setEtaLocationFrom(undefined)
    setEtaLocationTo(undefined)
    setEtaShow(false)
  }

  const geoFenceRenders = useMemo(() => {
    return isGeoFencesEnabled
      ? geoFences.data?.results?.map((geoFence) => (
          <GeoFenceMarker key={geoFence.id} geoFence={geoFence} />
        ))
      : null
  }, [geoFences.data?.results, isGeoFencesEnabled])

  // The task currently in focus mode. Resolved from the (status-filtered) layer set when present,
  // otherwise via a separate full fetch — so focusing a task never changes which tasks the layer
  // renders. Drives the route overlay, the docked progress popout, and hiding the geofence aside.
  const focusedTask = useFocusedTask(focusedTaskId, tasks?.results)

  // In focus mode the general geofence layer is decluttered away, but a stop's own geofence can
  // still be opened via the route's "View Geofence" action. That selects the geofence, and here we
  // draw just that one on the map so it's visible without leaving focus mode (BRI-1482).
  const focusedGeoFenceRender = useMemo(() => {
    if (!isGeoFencesEnabled || !focusedTask || selectedItem?.type !== 'geoFence') {
      return null
    }
    const geoFence = geoFences.data?.results?.find((result) => result.id === selectedItem.id)
    return geoFence ? <GeoFenceMarker key={geoFence.id} geoFence={geoFence} /> : null
  }, [isGeoFencesEnabled, focusedTask, selectedItem, geoFences.data?.results])

  // Opening a stop's geofence from inside focus mode, WITHOUT leaving focus. Selecting the geofence
  // makes focusedGeoFenceRender (above) draw that one geofence's shape on the otherwise decluttered
  // map; we then pan to the stop. Focus mode is preserved — see BRI-1482.
  //
  // This lives here rather than in TaskMapOverlay because it is Observatory-specific: it needs the
  // page store (which persists into the URL) and a geofence layer to reveal. The overlay takes it
  // as a prop so it stays mountable on pages that have neither.
  const viewGeofence = useCallback(
    (geoFenceId: number, position: google.maps.LatLngLiteral) => {
      setSelectedItem({ id: geoFenceId, type: 'geoFence' })
      setTimeout(() => {
        map?.panTo({ position })
      })
    },
    [map, setSelectedItem],
  )

  const TaskRenders = useMemo(() => {
    if (!isTasksEnabled) {
      return null
    }
    const allTasks = tasks?.results ?? []
    return (
      <>
        {/* Other tasks' start markers are hidden while a task is focused — only the focused
            task's route is shown, matching the geofence/asset declutter. */}
        {focusedTask ? null : <TaskStartLayer tasks={allTasks} />}
        {focusedTask ? (
          <TaskMapOverlay key={focusedTask.id} task={focusedTask} onViewGeofence={viewGeofence} />
        ) : null}
        {focusedTask ? (
          <TaskAssetEnRouteLine key={`en-route-${focusedTask.id}`} task={focusedTask} />
        ) : null}
      </>
    )
  }, [tasks, isTasksEnabled, focusedTask, viewGeofence])

  // Live percent-complete for the focused task only, keyed by the trip id (taskDefinition.id).
  const focusedTaskTripIds = useMemo(
    () => (focusedTask ? [focusedTask.taskDefinition.id] : undefined),
    [focusedTask],
  )
  const tripProgress = useTripUpdates(focusedTaskTripIds)

  const unlockMap = () => {
    setSelectedItem(undefined)
  }

  useEffect(() => {
    if (replayAssetId != undefined) {
      setMapMessage(
        <ReplayMapMessage
          assetId={replayAssetId}
          selectionStart={replaySelectionStartTime}
          selectionEnd={replaySelectionEndTime}
          onClose={() => {
            toggles.setAssets(true)
            asides.setHideAssets(false)
            asides.setHideGeoFences(false)
            // Restore the previously selected asset when exiting replay
            if (preReplaySelectedItem.current) {
              setSelectedItem(preReplaySelectedItem.current)
              preReplaySelectedItem.current = undefined
            }
            setReplayAsset(undefined)
          }}
        />,
      )
    } else if (showEta) {
      setMapMessage(<MapMessage message={'Viewing ETA'} onClose={etaOnClose} />)
    } else {
      setMapMessage(undefined)
    }
  }, [replayAssetId, showEta])

  // https://react.dev/reference/react/useContext#caveats
  const observatoryContextValues = useMemo(() => {
    return {
      assets,
      geoFences,
      unlockMap,
      createGeoFence: (geoFence) => createGeoFence(geoFence).finally(() => geoFences.mutate()),
      editGeoFence: (geoFenceId, geoFence) =>
        editGeoFence(geoFenceId, geoFence).finally(() => geoFences.mutate()),
      deleteGeoFence: (geoFence) => deleteGeoFence(geoFence).finally(() => geoFences.mutate()),
      // TODO: remove part referencing events once fully switched to dedicated "aside" api
      // We don't need the geofenceEvents in the context since migrated to the aside
      geoFenceEvents: {},
    } satisfies ContextType<typeof ObservatoryContext>
  }, [assets, geoFences])

  // https://react.dev/reference/react/useContext#caveats
  const activeAssetValues = useMemo(() => {
    return {
      activeAsset:
        selectedItem?.type === 'asset'
          ? assets.data?.results?.find((v) => v.id === selectedItem?.id)
          : undefined,
      setActiveAsset(asset) {
        setSelectedItem({ id: asset.id, type: 'asset' })
      },
      clearActiveAsset: () => setSelectedItem(undefined),
    } satisfies ContextType<typeof ActiveAssetContext>
  }, [selectedItem, assets.data?.results])

  const replayAsset = useObservatoryReplayAsset()

  const setReplaySelectionStartTime = usePersistentObservatoryPageStore(
    (state) => state.replay.setSelectionStart,
  )
  const setReplaySelectionEndTime = usePersistentObservatoryPageStore(
    (state) => state.replay.setSelectionEnd,
  )
  const stepSize = usePersistentObservatoryPageStore((state) => state.replay.granularity)
  const setStepSize = usePersistentObservatoryPageStore((state) => state.replay.setGranularity)

  // The observatory's replay bar spans a rolling recent window, independent of any one trip. Held
  // in state so the window doesn't shift under the user on every render.
  const [replayRangeStartTime] = useState(startOfHour(subHours(new Date(), 73)))
  const [replayRangeEndTime] = useState(startOfHour(addHours(new Date(), 1)))
  const [hoveredTimestamp, setHoveredTimestamp] = useState<number | null>(null)
  // https://react.dev/reference/react/useContext#caveats
  const replayValues = useMemo(() => {
    return {
      replayAsset,
      setReplayAsset: (asset: Asset | undefined) => {
        if (asset) {
          // Store the currently selected asset before entering replay
          preReplaySelectedItem.current = selectedItem
          setSelectedItem(undefined)
          setReplaySelectionStartTime(startOfDay(new Date()))
          setReplaySelectionEndTime(new Date())
        } else {
          // Restore the previously selected asset when exiting replay
          if (preReplaySelectedItem.current) {
            setSelectedItem(preReplaySelectedItem.current)
            preReplaySelectedItem.current = undefined
          }
        }

        setReplayAsset(asset?.id)
        asides.setHideAssets(true)
        asides.setHideGeoFences(true)
        toggles.setAssets(!asset)
      },
      available: true,
      hoveredTimestamp,
      setHoveredTimestamp,
    } satisfies ContextType<typeof ReplayContext>
  }, [replayAsset, assets.data?.results, selectedItem, hoveredTimestamp])

  const eventsGeoFence = useMemo(() => {
    if (geoFenceModalId != undefined && geoFences.data?.results) {
      return geoFences.data?.results?.find((geoFence) => geoFence.id === geoFenceModalId)
    }
    return undefined
  }, [geoFenceModalId, geoFences.data?.results])

  const reverseGeocodeMode = useFlag('reverseGeocodeMode', 'Google' as string)
  const isTagsEnabled = useFlag('assetTags', false)

  return (
    <ReplayContext.Provider value={replayValues}>
      <ObservatoryContext.Provider value={observatoryContextValues}>
        <ActiveAssetContext.Provider value={activeAssetValues}>
          <RouterInitializer />
          <div className={styles.observatoryWrapper}>
            <section
              className={styles.observatory}
              style={
                {
                  // Collapse both asides while a task is focused: the docked task progress popout
                  // owns the right, and the decluttered map (task route only) doesn't need the
                  // assets list on the left.
                  '--hide-left-aside': asides.hideAssets || !!focusedTask ? 1 : 0,
                  '--hide-right-aside':
                    !isGeoFencesEnabled || asides.hideGeoFences || !!focusedTask ? 1 : 0,
                } as CSSProperties
              }
            >
              <AssetsAside
                assets={assets}
                activeAsset={activeAssetValues.activeAsset}
                onAssetSelected={activeAssetValues.setActiveAsset}
              />
              <div
                className={styles.mapContainer}
                // Published so bottom-docked overlays (the task progress popout) can sit above the
                // replay bar instead of underneath it. 0 whenever replay isn't showing.
                style={{ '--map-bottom-overlay-height': `${replayBarHeight}px` } as CSSProperties}
              >
                <MapMessage onClose={() => setMapMessage(undefined)} message={mapMessage} />
                <MapHandler
                  center={mapPosition.center}
                  zoom={mapPosition.zoom}
                  mapOptions={{
                    clickableIcons: false,
                    mapTypeControl: false,
                    mapTypeControlOptions: {
                      mapTypeIds: ['roadmap', 'satellite', 'hybrid'],
                      position: window.google.maps.ControlPosition.LEFT_BOTTOM,
                      style: window.google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
                    },
                    streetViewControl: false,
                    zoomControl: true,
                    isFractionalZoomEnabled: true,
                  }}
                  mapCallback={updateMap}
                  onDragStart={() => {
                    setSelectedItem(undefined)
                  }}
                  onClick={() => {
                    // Deliberately does NOT exit Task Focus Mode — a stray map click shouldn't
                    // drop focus. Focus is only cleared via the task popout's close button.
                    setSelectedItem(undefined)
                  }}
                  onLongPress={(e) => {
                    if (e.latLng) {
                      reverseGeocode(e.latLng.lat(), e.latLng.lng(), reverseGeocodeMode)
                        .then((address) => {
                          if (address) {
                            setSearchLocation({
                              address: address,
                              location: e.latLng!.toJSON(),
                            })
                          } else {
                            setSearchLocation(undefined)
                          }
                        })
                        .catch((e) => Sentry.captureException(e))
                    }
                  }}
                >
                  <MapEmbedding position={window.google.maps.ControlPosition.TOP_LEFT} defer>
                    <MapSearchBar
                      className={styles.searchBar}
                      onSearchChanged={(l) => {
                        setSelectedItem(undefined)
                        setSearchLocation(l)
                      }}
                      searchInfo={searchLocation}
                    />
                  </MapEmbedding>
                  <MapEmbedding position={window.google.maps.ControlPosition.LEFT_CENTER}>
                    <button
                      type="button"
                      name="assets-tab"
                      className={[styles.asideToggle, styles.left].join(' ')}
                      onClick={() => asides.setHideAssets(!asides.hideAssets)}
                    >
                      <LuEllipsisVertical size={18} color="#9D9D9D" />
                    </button>
                  </MapEmbedding>

                  <FeatureFlag flag="featureGeofences">
                    <WhenFlag>
                      <MapEmbedding position={window.google.maps.ControlPosition.RIGHT_CENTER}>
                        <button
                          type="button"
                          name="geo-fence-tab"
                          aria-label="geo-fence-tab"
                          className={[styles.asideToggle, styles.right].join(' ')}
                          onClick={() => asides.setHideGeoFences(!asides.hideGeoFences)}
                        >
                          <LuEllipsisVertical size={18} color="#9D9D9D" />
                        </button>
                      </MapEmbedding>
                    </WhenFlag>
                  </FeatureFlag>
                  <FeatureFlag flag={'featureEta'}>
                    <WhenFlag>
                      {showEta && etaLocationFrom && etaLocationTo && (
                        <MapEmbedding
                          position={window.google.maps.ControlPosition.BLOCK_END_INLINE_CENTER}
                          className={styles.etaContainer}
                        >
                          <Eta
                            etaLocationFrom={etaLocationFrom}
                            etaLocationTo={etaLocationTo}
                            onClose={etaOnClose}
                          />
                        </MapEmbedding>
                      )}
                    </WhenFlag>
                  </FeatureFlag>
                  <LayerManager
                    toggles={{
                      assets: {
                        name: 'Assets',
                        value: toggles.assets,
                        setValue: toggles.setAssets,
                      },
                      ...(isGeoFencesEnabled
                        ? {
                            geoFences: {
                              name: 'Geo Fences',
                              value: toggles.geoFences,
                              setValue: toggles.setGeoFences,
                            },
                          }
                        : {}),
                      ...(isTasksEnabled
                        ? {
                            tasks: {
                              name: 'Tasks',
                              value: toggles.tasks,
                              setValue: toggles.setTasks,
                              childrenOptions: [
                                {
                                  name: 'Live Tasks',
                                  value: toggles.liveTasks,
                                  setValue: toggles.setLiveTasks,
                                },
                                {
                                  name: 'Scheduled Tasks',
                                  value: toggles.scheduledTasks,
                                  setValue: toggles.setScheduledTasks,
                                },
                              ],
                            },
                          }
                        : {}),
                      traffic: {
                        name: 'Traffic',
                        value: toggles.traffic,
                        setValue: toggles.setTraffic,
                      },
                      satellite: {
                        name: 'Satellite',
                        value: toggles.satellite,
                        setValue: toggles.setSatellite,
                      },
                    }}
                    vehicleLabels={{
                      showRegistration: vehicleLabels.showRegistration,
                      showShareReference: vehicleLabels.showShareReference,
                      setShowRegistration: vehicleLabels.setShowRegistration,
                      setShowShareReference: vehicleLabels.setShowShareReference,
                      clearVehicleLabels: vehicleLabels.clearVehicleLabels,
                      ...(isTagsEnabled
                        ? {
                            showTags: vehicleLabels.showTags,
                            setShowTags: vehicleLabels.setShowTags,
                          }
                        : {}),
                    }}
                  />
                  <MapIconLegend />

                  {/* While a task is focused, clear the general asset + geofence layers and keep
                      only the focused task's route and its assigned vehicle, to declutter. */}
                  {toggles.assets && !focusedTask ? <CollectiveAssetMarkers /> : null}
                  {focusedTask ? <TaskAssignedAssetMarker task={focusedTask} /> : null}
                  {toggles.geoFences && !focusedTask ? geoFenceRenders : null}
                  {focusedGeoFenceRender}
                  {toggles.tasks ? TaskRenders : null}
                  {!isEmpty(searchLocation) && showSearchMarker ? (
                    <MapSearchMarker position={searchLocation.location} />
                  ) : null}
                  {replayAsset != undefined ? (
                    <Replay
                      asset={replayAsset}
                      startDate={replaySelectionStartTime}
                      endDate={replaySelectionEndTime}
                      stepSize={`${stepSize}m`}
                      layers={replaySettings.route.layers}
                    />
                  ) : null}
                </MapHandler>

                <AssetPanel />

                {focusedTask ? (
                  <TaskProgressPopout
                    task={focusedTask}
                    showProgress
                    tripProgress={tripProgress[focusedTask.taskDefinition.id]}
                    onClose={() => setFocusedTaskId(undefined)}
                  />
                ) : null}

                {replayAsset != undefined ? (
                  <div ref={replayBarRef}>
                    <ReplayTimeline
                      asset={replayAsset}
                      rangeStartTime={replayRangeStartTime}
                      rangeEndTime={replayRangeEndTime}
                      selectionStartTime={replaySelectionStartTime}
                      selectionEndTime={replaySelectionEndTime}
                      setSelectionStartTime={setReplaySelectionStartTime}
                      setSelectionEndTime={setReplaySelectionEndTime}
                      stepSize={stepSize}
                      setStepSize={setStepSize}
                    />
                  </div>
                ) : null}
              </div>
              {isGeoFencesEnabled && <GeoFencesAside />}
            </section>
          </div>

          <GeofenceEventsModal
            geoFence={eventsGeoFence}
            onClose={() => setGeoFenceModalId(undefined)}
          />
        </ActiveAssetContext.Provider>
      </ObservatoryContext.Provider>
    </ReplayContext.Provider>
  )
}

export const ObservatoryPage = () => {
  return (
    <GoogleLoader>
      <Page />
    </GoogleLoader>
  )
}
